/* eslint-disable react-refresh/only-export-components */
import { useState } from "react";
import { useGetIdentity, useLogout } from "ra-core";
import { CheckCircle2, Copy, ExternalLink, LogOut, Phone } from "lucide-react";
import {
  SERA_PHONE,
  SERA_APP_URL,
} from "@/components/atomic-crm/misc/constants";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const projectRef = supabaseUrl
  ? new URL(supabaseUrl).hostname.split(".")[0]
  : null;
const webhookUrl = projectRef
  ? `https://${projectRef}.supabase.co/functions/v1/elevenlabs-webhook`
  : null;

const ElevenLabsAgentUrl = "https://elevenlabs.io/app/conversational-ai";

const CopyButton = ({ text }: { text: string }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      onClick={copy}
      className="flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded transition-colors"
      style={{
        background: copied ? "rgba(62,207,142,0.1)" : "#F4F4F4",
        color: copied ? "#1A8C5B" : "#8A8A8A",
      }}
    >
      {copied ? (
        <CheckCircle2 className="w-3 h-3" />
      ) : (
        <Copy className="w-3 h-3" />
      )}
      {copied ? "Copied" : "Copy"}
    </button>
  );
};

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div
    className="rounded-[10px] p-6"
    style={{ background: "#fff", border: "1px solid #E6E6E6" }}
  >
    <p
      className="text-[11px] font-medium uppercase tracking-[0.06em] mb-4"
      style={{ color: "#8A8A8A" }}
    >
      {title}
    </p>
    {children}
  </div>
);

const Row = ({
  label,
  value,
  mono,
  copyable,
  link,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
  copyable?: boolean;
  link?: string;
}) => (
  <div
    className="flex items-center justify-between py-3"
    style={{ borderBottom: "1px solid #F0F0F0" }}
  >
    <p className="text-[13px]" style={{ color: "#8A8A8A" }}>
      {label}
    </p>
    <div className="flex items-center gap-2">
      {value ? (
        <span
          className="text-[13px] font-medium"
          style={{
            color: "#252525",
            fontFamily: mono ? "Space Grotesk, monospace" : undefined,
          }}
        >
          {value}
        </span>
      ) : (
        <span className="text-[13px]" style={{ color: "#C8CDD6" }}>
          Not set
        </span>
      )}
      {copyable && value && <CopyButton text={value} />}
      {link && (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[11px] flex items-center gap-1"
          style={{ color: "#8A8A8A" }}
        >
          <ExternalLink className="w-3 h-3" />
        </a>
      )}
    </div>
  </div>
);

export const SettingsPage = () => {
  const { identity } = useGetIdentity();
  const logout = useLogout();

  const displayName =
    identity?.fullName ?? identity?.email?.split("@")[0] ?? "";

  return (
    <div className="min-h-screen" style={{ background: "#F7F8FA" }}>
      {/* Top bar */}
      <div
        className="sticky top-12 z-10 px-8 py-4"
        style={{ background: "#F7F8FA", borderBottom: "1px solid #E6E6E6" }}
      >
        <h1 className="text-[20px] font-bold" style={{ color: "#252525" }}>
          Settings
        </h1>
      </div>

      <div className="px-8 py-6 max-w-2xl space-y-5">
        {/* Sera voice line */}
        <Section title="Sera voice line">
          <Row label="Demo phone" value={SERA_PHONE} mono copyable />
          <Row
            label="App URL"
            value={SERA_APP_URL}
            copyable
            link={SERA_APP_URL}
          />
          <div
            className="mt-4 flex items-start gap-2.5 rounded-md p-3"
            style={{ background: "#F7F8FA" }}
          >
            <Phone
              className="w-4 h-4 mt-0.5 shrink-0"
              style={{ color: "#3ECF8E" }}
            />
            <p className="text-[13px]" style={{ color: "#8A8A8A" }}>
              Call the demo line to test Sera. New calls appear on the{" "}
              <a
                href="/conversations"
                className="underline"
                style={{ color: "#252525" }}
              >
                Calls page
              </a>{" "}
              in real time.
            </p>
          </div>
        </Section>

        {/* Integrations */}
        <Section title="Integrations">
          <Row
            label="Supabase"
            value={supabaseUrl ? "Connected" : "Not configured"}
            link={supabaseUrl ?? undefined}
          />
          <Row
            label="ElevenLabs agent"
            value="Manage in ElevenLabs"
            link={ElevenLabsAgentUrl}
          />
          {webhookUrl && (
            <div className="mt-4">
              <p
                className="text-[11px] font-medium uppercase tracking-[0.06em] mb-2"
                style={{ color: "#8A8A8A" }}
              >
                Post-call webhook URL
              </p>
              <div
                className="flex items-center justify-between rounded-md px-3 py-2.5"
                style={{ background: "#F7F8FA", border: "1px solid #E6E6E6" }}
              >
                <code
                  className="text-[12px] truncate mr-3"
                  style={{
                    color: "#252525",
                    fontFamily: "Space Grotesk, monospace",
                  }}
                >
                  {webhookUrl}
                </code>
                <CopyButton text={webhookUrl} />
              </div>
              <p className="text-[11px] mt-1.5" style={{ color: "#8A8A8A" }}>
                Paste this into your ElevenLabs agent under Agent settings →
                Post-call webhook.
              </p>
            </div>
          )}
        </Section>

        {/* Account */}
        <Section title="Account">
          <Row label="Name" value={displayName} />
          <Row label="Email" value={identity?.email ?? null} mono />
          <div className="mt-4">
            <button
              onClick={() => logout()}
              className="flex items-center gap-2 text-[13px] font-medium px-3 py-2 rounded-md"
              style={{ background: "#FEF2F2", color: "#D04339" }}
            >
              <LogOut className="w-3.5 h-3.5" />
              Sign out
            </button>
          </div>
        </Section>
      </div>
    </div>
  );
};

SettingsPage.path = "/settings";
